import { useState } from "react";

import { Button } from "mds/components/Button";
import { Menu, MenuItem } from "mds/components/Menu";
import { ChevronDownIcon } from "mds/icons";

export type SearchableDropdownProps<T> = {
  className?: string;
  items: T[];
  selectedId: string | null;
  onSelect: (item: T | null) => void;
  showAllOption?: boolean;
  allOptionLabel?: string;
  getItemId: (item: T) => string;
  getItemDisplayName: (item: T) => string;
  itemIcon: React.ReactNode;
  placeholder?: string;
  title?: string;
  disabled?: boolean;
};

/**
 * A generic searchable dropdown component that can be used for any type of items.
 */
export const SearchableDropdown = <T,>({
  className,
  items,
  selectedId,
  onSelect,
  showAllOption = true,
  allOptionLabel = "All",
  getItemId,
  getItemDisplayName,
  itemIcon,
  placeholder = "Search...",
  title = "Select",
  disabled = false,
}: SearchableDropdownProps<T>) => {
  const [searchTerm, setSearchTerm] = useState("");

  if (items.length <= 0) {
    return null;
  }

  // Filter items by search term
  const filteredItems = items.filter((item) =>
    getItemDisplayName(item).toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const sortedItems = filteredItems.sort((a, b) =>
    getItemDisplayName(a).localeCompare(getItemDisplayName(b)),
  );

  const selectedItem = selectedId ? items.find((item) => getItemId(item) === selectedId) : null;
  const hasChoices = items.length > 1 || showAllOption;

  const displayText = selectedItem ? getItemDisplayName(selectedItem) : allOptionLabel;

  const MenuButton = (
    <Button
      className={`gap-2 ${hasChoices ? "font-medium" : ""} ${className || ""}`}
      disabled={!hasChoices || disabled}
      kind={hasChoices ? "gray" : "grayInert"}
      size="s"
    >
      {itemIcon}
      {displayText}
      {hasChoices && <ChevronDownIcon />}
    </Button>
  );

  const onSelectItem = (item: T | null) => {
    onSelect(item);
    setSearchTerm(""); // Clear search when selecting
  };

  if (!hasChoices) {
    return MenuButton;
  }

  return (
    <Menu align="start" menuButton={MenuButton} title={title}>
      {/* Search input */}
      <div className="border-gray-200 border-b p-2">
        <input
          className="border-gray-300 focus:ring-blue-500 w-full rounded border px-2 py-1 text-sm focus:outline-none focus:ring-2"
          placeholder={placeholder}
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onClick={(e) => e.stopPropagation()}
        />
      </div>

      {showAllOption && (
        <MenuItem
          className={`${!selectedId ? "selected" : ""}`}
          key="all"
          onClick={() => onSelectItem(null)}
        >
          {allOptionLabel}
        </MenuItem>
      )}

      {sortedItems.length === 0 ? (
        <div className="text-gray-500 px-3 py-2 text-sm">No items found</div>
      ) : (
        sortedItems.map((item) => (
          <MenuItem
            className={`${selectedId === getItemId(item) ? "selected" : ""}`}
            key={getItemId(item)}
            onClick={() => onSelectItem(item)}
          >
            {getItemDisplayName(item)}
          </MenuItem>
        ))
      )}
    </Menu>
  );
};
